import React, { useEffect, useState } from 'react';
import { Star, Users, Package, GitCommit, Github, GitFork, Eye } from 'lucide-react';
import { Card } from './ui/card';
import axios from 'axios';

interface GitHubContributor {
  login: string;
  avatar_url: string;
  github_url: string;
  contributions: number;
}

interface GitHubStats {
  id: string;
  agent_id: string;
  stargazers_count: number;
  watchers_count: number;
  forks_count: number;
  total_commits_year: number;
  total_contributors: number;
  core_contributors: GitHubContributor[];
  description: string;
  last_github_update: string;
  owner_avatar_url: string;
  owner_bio: string;
  owner_company: string | null;
  owner_display_name: string;
  owner_followers: number;
  owner_github_url: string;
  owner_location: string | null;
  owner_name: string;
  repo_name: string;
}

interface GitHubStatsCardProps {
  agentId: string;
}

const GitHubStatsCard: React.FC<GitHubStatsCardProps> = ({ agentId }) => {
    const [stats, setStats] = useState<GitHubStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchGitHubStats = async () => {
            try {
                setIsLoading(true);
                console.log('🔍 Fetching GitHub stats for agent:', agentId);

                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/agents/${agentId}/github`
                );

                console.log('📥 Received GitHub data:', response.data);
                setStats(response.data);
                
            } catch (err) {
                console.error('❌ Error fetching GitHub stats:', err);
                setError(err instanceof Error ? err.message : 'Failed to load GitHub stats');
            } finally {
                setIsLoading(false);
            }
        };

        if (agentId) {
            fetchGitHubStats();
        }
    }, [agentId]);

    if (isLoading) {
        return (
            <Card className="w-[400px] bg-[#1c1b22] text-white p-6">
                <div className="animate-pulse flex flex-col gap-4">
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-700 rounded w-2/3"></div>
                </div>
            </Card>
        );
    }

    if (error || !stats) {
        return (
            <Card className="w-[200px] md:w-[400px]  text-white p-6">
                <div className="text-red-400">{'Sorry! No githubs stats found'}</div>
            </Card>
        );
    }

    return (
        <Card className="w-[400px]  text-white p-6 space-y-6">
            {/* Repository Owner Info */}
            <div className="flex items-center gap-4">
                <img 
                    src={stats.owner_avatar_url} 
                    alt={stats.owner_name}
                    className="w-12 h-12 rounded-full"
                />
                <div>
                    <h3 className="font-semibold">{stats.owner_display_name}</h3>
                    <p className="text-sm text-gray-400">{stats.owner_bio}</p>
                </div>
            </div>

            {/* Repository Stats */}
            <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span>{stats.stargazers_count?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <GitFork className="w-5 h-5 text-blue-500" />
                    <span>{stats.forks_count?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-500" />
                    <span>{stats.watchers_count?.toLocaleString()}</span>
                </div>
            </div>

            {/* Repository Description */}
            <div className="text-sm text-gray-300">
                {stats.description}
            </div>

            {/* Contributors Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-green-500" />
                        <span>{stats.total_contributors} contributors</span>
                    </div>
                    <span className="text-sm text-gray-400">
                        {new Date(stats.last_github_update).toLocaleDateString()}
                    </span>
                </div>

                {/* Core Contributors */}
                <div className="space-y-2">
                    <h4 className="text-sm text-gray-400">Top Contributors:</h4>
                    <div className="flex -space-x-2">
                        {stats.core_contributors.map((contributor, index) => (
                            <img
                                key={contributor.login}
                                src={contributor.avatar_url}
                                alt={contributor.login}
                                className="w-8 h-8 rounded-full border-2 border-[#1c1b22]"
                                title={`${contributor.login} (${contributor.contributions} commits)`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Commit Stats */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <GitCommit className="w-5 h-5 text-emerald-500" />
                    <span>{stats.total_commits_year?.toLocaleString()} commits this year</span>
                </div>
            </div>
        </Card>
    );
};

export default GitHubStatsCard;